import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Briefcase, DollarSign, Award, Check, ExternalLink, Share2, Bookmark, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { Progress } from '../components/ui/Progress';
import { cn } from '../lib/utils';
export function JobDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  return <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8" data-id="element-3404">
      <div className="max-w-7xl mx-auto" data-id="element-3405">
        <Button variant="ghost" className="mb-6 pl-0 hover:bg-transparent hover:text-atios-primary" onClick={() => navigate('/jobs')} data-id="element-3406">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3407" /> Back to Jobs
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-8" data-id="element-3408">
          <div className="space-y-8" data-id="element-3409">
            <div className="flex gap-6 items-start" data-id="element-3410">
              <Avatar className="w-16 h-16" fallback="TI" data-id="element-3411" />
              <div className="flex-1" data-id="element-3412">
                <div className="flex justify-between items-start" data-id="element-3413">
                  <div data-id="element-3414">
                    <h1 className="text-3xl font-semibold text-neutral-900 mb-2" data-id="element-3415">
                      Senior Backend Developer
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-sm" data-id="element-3416">
                      <span className="text-atios-primary font-medium cursor-pointer hover:underline" data-id="element-3417">
                        PT Teknologi Indonesia
                      </span>
                      <span className="flex items-center text-neutral-600" data-id="element-3418">
                        <MapPin className="w-4 h-4 mr-1" data-id="element-3419" /> Jakarta, Indonesia
                      </span>
                      <span className="flex items-center text-neutral-600" data-id="element-3420">
                        <Clock className="w-4 h-4 mr-1" data-id="element-3421" /> Posted 5 days ago
                      </span>
                    </div>
                  </div>
                  <Badge variant="success" className="text-sm px-3 py-1 hidden sm:flex" data-id="element-3422">
                    95% Match
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3423">
              {[{
              label: 'Job Type',
              value: 'Full-time',
              icon: Briefcase
            }, {
              label: 'Salary',
              value: 'Rp 15M - 20M',
              icon: DollarSign
            }, {
              label: 'Experience',
              value: '3-5 years',
              icon: Award
            }, {
              label: 'Location',
              value: 'Jakarta',
              icon: MapPin
            }].map((item, i) => <div key={i} className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 hover:border-atios-primary transition-colors" data-id="element-3424">
                  <div className="flex items-center gap-2 text-neutral-600 mb-2 text-xs uppercase tracking-wider" data-id="element-3425">
                    <item.icon className="w-4 h-4" data-id="element-3426" /> {item.label}
                  </div>
                  <div className="font-semibold text-neutral-900" data-id="element-3427">
                    {item.value}
                  </div>
                </div>)}
            </div>

            <section className="space-y-4" data-id="element-3428">
              <h2 className="text-xl font-semibold text-neutral-900 border-b border-neutral-200 pb-2" data-id="element-3429">
                Job Description
              </h2>
              <div className="text-neutral-700 space-y-4 leading-relaxed" data-id="element-3430">
                <p data-id="element-3431">
                  We are looking for an experienced Senior Backend Developer to
                  join our engineering team. You will be responsible for
                  designing, developing, and maintaining scalable and reliable
                  backend systems.
                </p>
                <div data-id="element-3432">
                  <h3 className="font-semibold text-neutral-900 mb-2" data-id="element-3433">
                    Key Responsibilities:
                  </h3>
                  <ul className="list-disc list-inside space-y-1 pl-2" data-id="element-3434">
                    <li data-id="element-3435">Design and implement RESTful APIs</li>
                    <li data-id="element-3436">Optimize database performance and queries</li>
                    <li data-id="element-3437">Collaborate with frontend and product teams</li>
                    <li data-id="element-3438">Code review and mentor junior developers</li>
                    <li data-id="element-3439">Ensure application security and scalability</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="space-y-4" data-id="element-3440">
              <h2 className="text-xl font-semibold text-neutral-900 border-b border-neutral-200 pb-2" data-id="element-3441">
                Requirements
              </h2>
              <ul className="space-y-3" data-id="element-3442">
                {['Minimum 3 years backend development experience', 'Proficient in Node.js and Express.js', 'Experience with PostgreSQL or MySQL', 'Familiar with Docker and CI/CD', 'Strong problem-solving skills', 'Effective communication'].map((req, i) => <li key={i} className="flex items-start gap-3 text-neutral-700" data-id="element-3443">
                    <div className="mt-1 bg-success-light rounded-full p-0.5" data-id="element-3444">
                      <Check className="w-3 h-3 text-success" data-id="element-3445" />
                    </div>
                    {req}
                  </li>)}
              </ul>
            </section>

            <section className="space-y-4" data-id="element-3446">
              <h2 className="text-xl font-semibold text-neutral-900 border-b border-neutral-200 pb-2" data-id="element-3447">
                Required Skills
              </h2>
              <div className="bg-atios-light p-6 rounded-lg border border-atios-primary/30" data-id="element-3448">
                <p className="text-sm text-neutral-700 mb-4" data-id="element-3449">
                  You have{' '}
                  <span className="text-atios-primary font-bold" data-id="element-3450">4 of 6</span>{' '}
                  required skills
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-3451">
                  {[{
                  name: 'Backend Development',
                  match: true
                }, {
                  name: 'Node.js',
                  match: true
                }, {
                  name: 'PostgreSQL',
                  match: true
                }, {
                  name: 'Docker',
                  match: true
                }, {
                  name: 'Kubernetes',
                  match: false
                }, {
                  name: 'AWS',
                  match: false
                }].map(skill => <Badge key={skill.name} variant={skill.match ? 'success' : 'default'} className={cn('flex items-center gap-2 px-3 py-1.5', !skill.match && 'bg-neutral-100 text-neutral-600')} data-id="element-3452">
                      {skill.name}
                      {skill.match && <Check className="w-3 h-3" data-id="element-3453" />}
                    </Badge>)}
                </div>
              </div>
            </section>

            <section className="space-y-4" data-id="element-3454">
              <h2 className="text-xl font-semibold text-neutral-900 border-b border-neutral-200 pb-2" data-id="element-3455">
                About Company
              </h2>
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6" data-id="element-3456">
                <div className="flex items-center gap-4 mb-4" data-id="element-3457">
                  <Avatar className="w-12 h-12" fallback="TI" data-id="element-3458" />
                  <div data-id="element-3459">
                    <h3 className="font-semibold text-neutral-900" data-id="element-3460">
                      PT Teknologi Indonesia
                    </h3>
                    <a href="#" className="text-atios-primary text-sm hover:underline flex items-center gap-1" data-id="element-3461">
                      www.teknologiindonesia.com{' '}
                      <ExternalLink className="w-3 h-3" data-id="element-3462" />
                    </a>
                  </div>
                </div>
                <p className="text-neutral-600 mb-6" data-id="element-3463">
                  PT Teknologi Indonesia is a leading technology company focused
                  on developing digital solutions for enterprises. We have a
                  passionate team and a culture that supports growth.
                </p>
                <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-3464">
                  <div data-id="element-3465">
                    <p className="text-neutral-500" data-id="element-3466">Industry</p>
                    <p className="text-neutral-900 font-medium" data-id="element-3467">
                      Software Development
                    </p>
                  </div>
                  <div data-id="element-3468">
                    <p className="text-neutral-500" data-id="element-3469">Company Size</p>
                    <p className="text-neutral-900 font-medium" data-id="element-3470">
                      100-500 employees
                    </p>
                  </div>
                  <div data-id="element-3471">
                    <p className="text-neutral-500" data-id="element-3472">Office Location</p>
                    <p className="text-neutral-900 font-medium" data-id="element-3473">
                      South Jakarta
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="space-y-6" data-id="element-3474">
            <Card className="sticky top-24" data-id="element-3475">
              <CardContent className="p-6 space-y-6" data-id="element-3476">
                <div className="text-center" data-id="element-3477">
                  <h3 className="font-semibold text-neutral-900 mb-4" data-id="element-3478">
                    Interested in this job?
                  </h3>
                  <div className="relative w-32 h-32 mx-auto mb-4 flex items-center justify-center" data-id="element-3479">
                    <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36" data-id="element-3480">
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#E5E7EB" strokeWidth="3" data-id="element-3481" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#30A5DB" strokeWidth="3" strokeDasharray="95, 100" data-id="element-3482" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-3483">
                      <span className="text-3xl font-semibold text-atios-primary" data-id="element-3484">
                        95%
                      </span>
                      <span className="text-xs text-neutral-500" data-id="element-3485">Match</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3" data-id="element-3486">
                  <div className="space-y-1" data-id="element-3487">
                    <div className="flex justify-between text-xs" data-id="element-3488">
                      <span className="text-neutral-600" data-id="element-3489">Skills Match</span>
                      <span className="text-neutral-900" data-id="element-3490">95%</span>
                    </div>
                    <Progress value={95} className="h-1.5" data-id="element-3491" />
                  </div>
                  <div className="space-y-1" data-id="element-3492">
                    <div className="flex justify-between text-xs" data-id="element-3493">
                      <span className="text-neutral-600" data-id="element-3494">Experience Match</span>
                      <span className="text-neutral-900" data-id="element-3495">90%</span>
                    </div>
                    <Progress value={90} className="h-1.5" data-id="element-3496" />
                  </div>
                  <div className="space-y-1" data-id="element-3497">
                    <div className="flex justify-between text-xs" data-id="element-3498">
                      <span className="text-neutral-600" data-id="element-3499">Location Match</span>
                      <span className="text-neutral-900" data-id="element-3500">100%</span>
                    </div>
                    <Progress value={100} className="h-1.5" data-id="element-3501" />
                  </div>
                </div>

                <div className="space-y-3 pt-2" data-id="element-3502">
                  <Button fullWidth size="lg" className="font-semibold" data-id="element-3503">
                    Apply Now
                  </Button>
                  <Button fullWidth variant="secondary" data-id="element-3504">
                    <Bookmark className="w-4 h-4 mr-2" data-id="element-3505" /> Save Job
                  </Button>
                  <Button fullWidth variant="secondary" data-id="element-3506">
                    <Share2 className="w-4 h-4 mr-2" data-id="element-3507" /> Share
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card data-id="element-3508">
              <CardContent className="p-6" data-id="element-3509">
                <div className="flex items-center gap-3 mb-3" data-id="element-3510">
                  <div className="bg-success-light p-2 rounded-full" data-id="element-3511">
                    <Check className="w-4 h-4 text-success" data-id="element-3512" />
                  </div>
                  <div data-id="element-3513">
                    <h4 className="font-semibold text-neutral-900 text-sm" data-id="element-3514">
                      Already Applied
                    </h4>
                    <p className="text-xs text-neutral-500" data-id="element-3515">2 days ago</p>
                  </div>
                </div>
                <Button variant="link" className="p-0 h-auto text-atios-primary text-sm" data-id="element-3516">
                  View Application Status →
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}